import * as React from 'react';
import { Columns3, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumn } from '../data-table/data-table';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ColumnsToggleProps<T> {
  canReset?: boolean;
  columns: DataTableColumn<T>[];
  hiddenColumns: string[];
  onClose?: () => void;
  resetToDefault: () => void;
  toggleColumn: (columnKey: string) => void;
}

interface ColumnRowProps<T> {
  col: DataTableColumn<T>;
  hiddenColumns: string[];
  toggleColumn: (key: string) => void;
}

// ─── Components ────────────────────────────────────────────────────────────────

function ColumnRow<T>({ col, hiddenColumns, toggleColumn }: ColumnRowProps<T>): React.JSX.Element {
  const isVisible = !hiddenColumns.includes(col.key);

  return (
    <li className="flex items-center justify-between">
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox
          checked={isVisible}
          onCheckedChange={(): void => {
            toggleColumn(col.key);
          }}
        />
        <span>{col.header}</span>
      </label>
    </li>
  );
}

export function ColumnsToggle<T>({
  canReset,
  columns,
  hiddenColumns,
  onClose,
  resetToDefault,
  toggleColumn,
}: ColumnsToggleProps<T>): React.JSX.Element {
  const { t } = useTranslation('common');

  const hiddenCount = hiddenColumns.length;
  const showReset = canReset ?? hiddenCount > 0;

  function handleOpenChange(open: boolean): void {
    if (!open) {
      onClose?.();
    }
  }

  return (
    <Popover onOpenChange={handleOpenChange}>
      <span className="relative inline-flex items-center">
        <PopoverTrigger asChild>
          <Button
            aria-label={t('columnsToggle.title')}
            className="h-9 w-9 shadow-none"
            size="icon"
            type="button"
            variant="outline"
          >
            <Columns3 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        {hiddenCount > 0 && (
          <span
            aria-label={t('columnsToggle.hiddenCount', { count: hiddenCount })}
            className="pointer-events-none absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground"
          >
            {hiddenCount}
          </span>
        )}
      </span>
      <PopoverContent align="end" className="w-64 p-3" side="bottom">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('columnsToggle.title')}
        </p>
        <ul className="space-y-2">
          {columns
            .filter((col) => !col.alwaysOn)
            .map((col) => (
              <ColumnRow col={col} hiddenColumns={hiddenColumns} key={col.key} toggleColumn={toggleColumn} />
            ))}
        </ul>
        {showReset && (
          <button
            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={resetToDefault}
            type="button"
          >
            <RotateCcw className="h-3 w-3" />
            {t('columnsToggle.resetToDefault')}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
